<template>
  <footer class="app-footer">
    <div class="footer-container">
      <div class="footer-grid">
        <!-- Brand -->
        <div class="footer-brand-section">
          <div class="brand-logo">
            <logo variant="full" theme="auto" size="lg" />
          </div>
          <p class="brand-description">
            IS-compliant structural calculators that show every formula, substitution, and clause.
          </p>
          <nav class="social-links" aria-label="Social media">
            <a
              v-for="social in socialLinks"
              :key="social.name"
              :href="social.url"
              target="_blank"
              rel="noopener noreferrer"
              class="social-link"
              :aria-label="social.name"
            >
              <v-icon size="16">{{ social.icon }}</v-icon>
            </a>
          </nav>
        </div>

        <!-- Popular Calculators -->
        <nav class="nav-section" aria-labelledby="footer-calculators-title">
          <h3 id="footer-calculators-title" class="nav-title">Popular Calculators</h3>
          <div class="nav-links">
            <router-link to="/calculators/concrete/beam-design" class="nav-link">RC Beam Design</router-link>
            <router-link to="/calculators/concrete/column-design" class="nav-link">RC Column Design</router-link>
            <router-link to="/calculators/steel/base-plate" class="nav-link">Steel Base Plate</router-link>
            <router-link to="/calculators" class="nav-link featured">
              View all calculators &rarr;
            </router-link>
          </div>
        </nav>

        <!-- Resources -->
        <nav class="nav-section" aria-labelledby="footer-resources-title">
          <h3 id="footer-resources-title" class="nav-title">Resources</h3>
          <div class="nav-links">
            <a href="/documentation" class="nav-link">Documentation</a>
            <router-link to="/blog" class="nav-link">Blog</router-link>
          </div>
        </nav>

        <!-- Company -->
        <nav class="nav-section" aria-labelledby="footer-company-title">
          <h3 id="footer-company-title" class="nav-title">Company</h3>
          <div class="nav-links">
            <router-link to="/about" class="nav-link">About</router-link>
            <router-link to="/contact" class="nav-link">Contact</router-link>
          </div>
        </nav>

        <!-- Legal -->
        <nav class="nav-section" aria-labelledby="footer-legal-title">
          <h3 id="footer-legal-title" class="nav-title">Legal</h3>
          <div class="nav-links">
            <router-link to="/privacy" class="nav-link">Privacy</router-link>
            <router-link to="/terms" class="nav-link">Terms</router-link>
            <router-link to="/disclaimer" class="nav-link">Engineering disclaimer</router-link>
          </div>
        </nav>
      </div>

      <div class="footer-bottom">
        <span class="copyright">© {{ currentYear }} StructEngine · Made in India</span>
        <span class="version">v0.1.0-beta</span>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import Logo from '@/components/common/Logo.vue'

const currentYear = new Date().getFullYear()

const socialLinks = [
  { name: 'LinkedIn', icon: 'mdi-linkedin', url: 'https://www.linkedin.com/company/structengine' },
  { name: 'Twitter', icon: 'mdi-twitter', url: 'https://twitter.com/structengine' },
  { name: 'YouTube', icon: 'mdi-youtube', url: 'https://youtube.com/@structengine' },
]
</script>

<style scoped>
.app-footer {
  background: var(--bg-canvas);
  color: var(--text-body);
  border-top: 1px solid var(--border-subtle);
}

.footer-container {
  max-width: 1440px;
  margin: 0 auto;
  padding: 48px 24px 0;
}

.footer-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  gap: 32px;
  margin-bottom: 48px;
}

/* Brand */
.footer-brand-section {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.brand-logo {
  line-height: 0;
  margin-top: -64px;
}

.brand-description {
  font-size: var(--font-size-sm);
  line-height: 1.6;
  color: var(--text-muted);
  margin: 0;
  margin-top: -40px;
}

.social-links {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}

.social-link {
  width: 34px;
  height: 34px;
  background: transparent;
  border: 1px solid color-mix(in srgb, var(--border-subtle) 72%, transparent);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  text-decoration: none;
  transition: border-color 0.2s, color 0.2s;
}

.social-link:hover {
  border-color: color-mix(in srgb, var(--accent) 68%, var(--border-subtle));
  color: var(--accent);
}

/* Nav sections */
.nav-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.nav-title {
  font-size: var(--font-size-micro);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin: 0;
}

.nav-links {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.nav-link {
  font-size: var(--font-size-sm);
  color: var(--text-body);
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-color: transparent;
  transition: text-decoration-color 0.15s, color 0.15s;
}

.nav-link:hover {
  color: var(--text-primary);
  text-decoration-color: var(--text-body);
}

.nav-link.featured {
  color: var(--accent);
  text-decoration-color: transparent;
  font-weight: 500;
}

.nav-link.featured:hover {
  text-decoration-color: var(--accent);
}

/* Bottom bar */
.footer-bottom {
  border-top: 1px solid var(--border-subtle);
  padding: 20px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.copyright {
  font-size: var(--font-size-caption);
  color: var(--text-secondary);
}

.version {
  font-size: var(--font-size-caption);
  color: var(--text-muted);
  font-family: var(--font-family-mono);
}

/* Responsive */
@media (max-width: 1023px) {
  .footer-grid {
    grid-template-columns: 1fr 1fr;
    gap: 40px;
  }

  .footer-brand-section {
    grid-column: 1 / -1;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 24px;
  }
}

@media (max-width: 639px) {
  .footer-grid {
    grid-template-columns: 1fr 1fr;
    gap: 32px;
  }

  .footer-brand-section {
    grid-column: 1 / -1;
    flex-direction: column;
  }

  .footer-bottom {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
}
</style>
